import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../../../../services/api';
import type {
  ChecklistSummary,
  ChecklistDetail,
  ChecklistItem,
  ProcessedItems,
  ChecklistStats,
  SortColumn,
  SortDirection,
} from '../types';
import { filterItems, sortItems, calculateStats } from '../utils/helpers';

/**
 * Hook to fetch all checklists
 */
export function useChecklists() {
  return useQuery({
    queryKey: ['checklists'],
    queryFn: () => api.getChecklists(),
    select: (data): ChecklistSummary[] => data?.checklists || [],
  });
}

/**
 * Hook to fetch a single checklist with items
 */
export function useChecklist(checklistId: string | null) {
  return useQuery({
    queryKey: ['checklist', checklistId],
    queryFn: () => api.getChecklist(checklistId!),
    enabled: !!checklistId,
    select: (data): ChecklistDetail | undefined => data?.checklist,
  });
}

/**
 * Hook to process and filter checklist items
 */
export function useProcessedItems(
  items: ChecklistItem[] | undefined,
  filter: string,
  sortColumn: SortColumn,
  sortDirection: SortDirection
): ProcessedItems {
  return useMemo(() => {
    if (!items) return { active: [], completed: [] };

    // Apply filter
    let filtered = filterItems(items, filter);

    // Apply sort
    filtered = sortItems(filtered, sortColumn, sortDirection);

    // Separate active and completed
    const active = filtered.filter(item => item.status !== 'installed');
    const completed = filtered.filter(item => item.status === 'installed');

    return { active, completed };
  }, [items, filter, sortColumn, sortDirection]);
}

/**
 * Hook to check if all active items are on watchlist
 */
export function useAllActiveOnWatchlist(activeItems: ChecklistItem[]): boolean {
  return useMemo(() => {
    const itemsWithCode = activeItems.filter(i => i.productCode);
    if (itemsWithCode.length === 0) return false;
    return itemsWithCode.every(i => i.onWatchlist);
  }, [activeItems]);
}

/**
 * Hook to calculate aggregate stats
 */
export function useChecklistStats(
  checklists: ChecklistSummary[],
  allItems?: ChecklistItem[]
): ChecklistStats {
  return useMemo(() => {
    return calculateStats(checklists, allItems);
  }, [checklists, allItems]);
}
